import {gql} from '@apollo/client';
import {objectFromEntries} from '@benchling/js-utils';
import stringify from 'json-stable-stringify';

import {assert} from 'Asserts';
import {SampleRestrictionStatus} from 'constants/SampleStatusConstants';
import {PlateSchemaType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {doQueryWithoutCache} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {getRestrictedSampleUsersHeaderText} from 'inventory/sample-status/RestrictedSampleUsersTextUtil';
import {SampleControlsFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevel';
import {hasMinimumSCRFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevelUtil';
import {isFixedPlateWell} from 'inventory/sample-status/SampleStatusPresentationUtils';
import {
  GetContainerSampleRoles,
  GetContainerSampleRolesVariables,
} from 'notebook/integrations/samples/transfer-table-columns/__generated/GetContainerSampleRoles';
import {
  GetPlateWithWellSampleRoles,
  GetPlateWithWellSampleRolesVariables,
} from 'notebook/integrations/samples/transfer-table-columns/__generated/GetPlateWithWellSampleRoles';
import {extractContainerId} from 'notebook/integrations/samples/transfer-table-columns/extractContainerId';
import extractPlateId from 'notebook/integrations/samples/transfer-table-columns/extractPlateId';
import makeCustomResolutionResultFromTypedValue from 'notebook/integrations/samples/transfer-table-columns/makeCustomResolutionResultFromTypedValue';
import {PartiesColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {ValWithErrorObject} from 'notebook/tables/errors/TableErrorConstants';
import {
  CustomResolutionQueryType,
  InventoryTableRestrictedSampleUsersFromSourceOrDestinationContainerQuery,
  InventoryTableRestrictedSampleUsersFromSourcePlateQuery,
  InventoryTableRestrictionStatusFromSourceOrDestinationContainerQuery,
  InventoryTableRestrictionStatusFromSourcePlateQuery,
  InventoryTableSampleOwnersFromSourceOrDestinationContainerQuery,
  InventoryTableSampleOwnersFromSourcePlateQuery,
  ServerResolutionResult,
} from 'notebook/tables/string-resolution/StringResolutionConstants';
import {CustomEval} from 'notebook/tables/TableConstants';
import {ResolutionQueryType} from 'notebook/tables/TableStoreTransforms';
import {
  formatTypedValue,
  getPartyIdsFromTypedValue,
  getTypedValueFromPartySummaries,
  getTypedValueFromSampleRestrictionStatus,
  isTypedValueEmpty,
} from 'notebook/tables/typed-values/DataTypes';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {
  makeTypedValueFromCustomResolvedValue,
  ResolveQueryFn,
  ResolveTypedValueToTypeFn,
} from 'notebook/tables/typed-values/TypedValueResolution';
import {
  SampleRestrictionStatusTypedValue,
  StringTypedValue,
} from 'notebook/tables/typed-values/TypedValueTypes';
import {InvalidPositionError, parseGridPosition} from 'samples/SampleUtil';
import lruMemoize from 'util/lruMemoize';

type PlateOrInaccessible = GetPlateWithWellSampleRoles['plates'][number];

type PlateGQL = ExtractByTypename<PlateOrInaccessible, 'PlateNode'>;

type ContainerOrInaccessible = GetContainerSampleRoles['containers'][number];

type ContainerGQL = ExtractByTypename<ContainerOrInaccessible, 'ContainerNode'>;

type PartySummary = ContainerGQL['sampleRoles']['sampleOwners'][number];

export const GET_CONTAINER_SAMPLE_ROLES = gql`
  query GetContainerSampleRoles($containerIds: [StrictString!]!) @skipAudit @engComponent(name: INVENTORY) {
    containers(ids: $containerIds) {
      ... on ContainerNode {
        id
        sampleRoles {
          id
          sampleOwners {
            ... on PartyInterface {
              id
              name
              handle
            }
          }
          restrictedSampleUsers {
            ... on PartyInterface {
              id
              name
              handle
            }
          }
        }
        restrictionStatus
        storableSchema {
          ... on StorableSchemaInterface {
            id
          }
        }
        parentStorable {
          ... on StorableInterface {
            id
            storableSchema {
              ... on PlateSchemaNode {
                id
                plateSchemaType
              }
            }
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const GET_PLATE_WITH_WELL_SAMPLE_ROLES_QUERY = gql`
  query GetPlateWithWellSampleRoles($plateIds: [StrictString!]!) @skipAudit @engComponent(name: INVENTORY) {
    plates(ids: $plateIds) {
      ... on PlateNode {
        id
        storableSchema {
          ... on PlateSchemaNode {
            id
            height
            width
            plateSchemaType
          }
        }
        cells {
          container {
            ... on ContainerNode {
              id
              sampleRoles {
                id
                sampleOwners {
                  ... on PartyInterface {
                    id
                    name
                    handle
                  }
                }
                restrictedSampleUsers {
                  ... on PartyInterface {
                    id
                    name
                    handle
                  }
                }
              }
              restrictionStatus
            }
            ...InaccessibleInfoGraphQL
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

// To mock our GraphQL call, we need to wrap it in a method.
// This is different from most of our GraphQL tests, where we mock the Apollo cache.
// Here, mocking the cache won't work, because our fetch policy is `'no-cache'`.
export const stubbableFunctions = {
  async doContainerQuery(containerId: string): Promise<GetContainerSampleRoles> {
    return await doQueryWithoutCache<GetContainerSampleRoles, GetContainerSampleRolesVariables>(
      {query: GET_CONTAINER_SAMPLE_ROLES},
      {variables: {containerIds: [containerId]}}
    );
  },

  async doSourceAndDestinationContainerQuery(
    sourceContainerId: string,
    destinationContainerId: string
  ): Promise<GetContainerSampleRoles> {
    return await doQueryWithoutCache<GetContainerSampleRoles, GetContainerSampleRolesVariables>(
      {query: GET_CONTAINER_SAMPLE_ROLES},
      {variables: {containerIds: [sourceContainerId, destinationContainerId]}}
    );
  },

  async doPlateQuery(plateId: string): Promise<GetPlateWithWellSampleRoles> {
    return await doQueryWithoutCache<GetPlateWithWellSampleRoles, GetPlateWithWellSampleRolesVariables>(
      {query: GET_PLATE_WITH_WELL_SAMPLE_ROLES_QUERY},
      {variables: {plateIds: [plateId]}}
    );
  },
};

type SampleRoleKey = 'sampleOwners' | 'restrictedSampleUsers';

interface PlateWell {
  plate: PlateOrInaccessible;
  position: string;
}
type ContainerOrPlateWell = {container: ContainerOrInaccessible} | PlateWell;

function isPlateWell(containerOrWell: ContainerOrPlateWell): containerOrWell is PlateWell {
  return (containerOrWell as PlateWell).plate != null;
}

function containerIsValid(
  container: ContainerOrInaccessible,
  containerSchemaId?: string
): container is ContainerGQL {
  return (
    !isInaccessible(container) &&
    (containerSchemaId == null || containerSchemaId === container.storableSchema.id)
  );
}

function getContainerSampleRolesIfValid(
  container: ContainerOrInaccessible | null,
  sampleRoleKey: SampleRoleKey,
  containerSchemaId?: string
): Array<PartySummary> | null {
  // Null container means it's being created & has no existing sample roles
  if (!container) {
    return [];
  }
  if (!containerIsValid(container, containerSchemaId)) {
    return null;
  }
  return container.sampleRoles[sampleRoleKey];
}

function getPlateWellCoordinatesIfValid(
  plate: PlateGQL,
  position: string
): {rowIndex: number; columnIndex: number} | null {
  assert(plate.storableSchema.__typename === 'PlateSchemaNode', 'Expected a plate schema');

  let rowIndex: number;
  let columnIndex: number;
  try {
    ({rowIndex, columnIndex} = parseGridPosition({
      position,
      gridHeight: plate.storableSchema.height,
      gridWidth: plate.storableSchema.width,
    }));
  } catch (e) {
    if (e instanceof InvalidPositionError) {
      return null;
    } else {
      throw e;
    }
  }
  return {rowIndex, columnIndex};
}

function getPlateWellSampleRolesIfValid(
  plate: PlateOrInaccessible,
  position: string,
  sampleRoleKey: SampleRoleKey
): Array<PartySummary> | null {
  if (isInaccessible(plate)) {
    return null;
  }
  assert(plate.storableSchema.__typename === 'PlateSchemaNode', 'Expected a plate schema');
  if (plate.storableSchema.plateSchemaType === PlateSchemaType.FIXED_PLATE) {
    return [];
  }

  const coordinatesOrNull = getPlateWellCoordinatesIfValid(plate, position);
  if (coordinatesOrNull == null) {
    return null;
  }
  const {rowIndex, columnIndex} = coordinatesOrNull;
  const plateWell = plate.cells[rowIndex][columnIndex]?.container;
  if (isInaccessible(plateWell)) {
    return null;
  }
  return plateWell?.sampleRoles[sampleRoleKey] ?? [];
}

function getContainerRestrictionStatusIfValid(
  container: ContainerOrInaccessible | null,
  containerSchemaId?: string
): SampleRestrictionStatus | null {
  // Null container means it's being created & has no existing sample controls
  if (!container) {
    return SampleRestrictionStatus.UNRESTRICTED;
  }
  if (!containerIsValid(container, containerSchemaId)) {
    return null;
  }
  return container.restrictionStatus;
}

function getPlateWellRestrictionStatusIfValid(
  plate: PlateOrInaccessible,
  position: string
): SampleRestrictionStatus | null {
  if (isInaccessible(plate)) {
    return null;
  }
  const coordinatesOrNull = getPlateWellCoordinatesIfValid(plate, position);
  if (coordinatesOrNull == null) {
    return null;
  }
  const {rowIndex, columnIndex} = coordinatesOrNull;

  // Container restriction status already handles returning N/A for well plate wells
  const plateWell = plate.cells[rowIndex][columnIndex]?.container;
  if (isInaccessible(plateWell)) {
    return null;
  }
  return plateWell?.restrictionStatus ?? null;
}

function canHaveSampleControls(container: ContainerOrInaccessible | null): boolean {
  return container != null && !isInaccessible(container) && !isFixedPlateWell(container);
}

function canHaveRestrictedSampleUsers(
  sourceStorable: ContainerOrPlateWell | null,
  destinationContainer: ContainerOrInaccessible | null,
  isSpecifyingSampleOwners: boolean | null,
  specifiedRestrictionStatus: SampleRestrictionStatusTypedValue['status'] | null
): {canHaveAuthUsers: boolean; possibleErrorText: string} {
  // If decoupling is enabled (WIP flag), then check restriction status. Otherwise, check sample owners.
  if (hasMinimumSCRFeatureLevel(SampleControlsFeatureLevel.V2)) {
    const decoupledErrorText = `${getRestrictedSampleUsersHeaderText()} cannot be set without a "Restricted" status`;
    return {
      canHaveAuthUsers:
        // If restriction status is specified, it must be restricted. If not specified, it can be inferred to
        // restricted by setting AUs.
        specifiedRestrictionStatus === SampleRestrictionStatus.RESTRICTED || !specifiedRestrictionStatus,
      possibleErrorText: decoupledErrorText,
    };
  } else {
    const coupledErrorText = `${getRestrictedSampleUsersHeaderText()} cannot be set without sample owners`;
    // Table has a sample owners column; isSpecifyingSampleOwners will be true if it's non-empty
    if (isSpecifyingSampleOwners != null) {
      return {canHaveAuthUsers: isSpecifyingSampleOwners, possibleErrorText: coupledErrorText};
    }
    // If table isn't specifying sample owners, check if they'd be inferred
    const sampleOwners = getUnionOfSampleRolesFromSourceAndDestination({
      source: sourceStorable,
      destinationContainer,
      sampleRoleKey: 'sampleOwners',
    });
    return {
      canHaveAuthUsers: sampleOwners != null && sampleOwners.length > 0,
      possibleErrorText: coupledErrorText,
    };
  }
}

function getUnionOfSampleRolesFromSourceAndDestination({
  source,
  destinationContainer,
  sampleRoleKey,
  destinationContainerSchemaId,
}: {
  source: ContainerOrPlateWell | null;
  destinationContainer: ContainerOrInaccessible | null;
  sampleRoleKey: SampleRoleKey;
  destinationContainerSchemaId?: string;
}): Array<PartySummary> | null {
  const sourceSampleRoles =
    source == null
      ? []
      : isPlateWell(source)
        ? getPlateWellSampleRolesIfValid(source.plate, source.position, sampleRoleKey)
        : getContainerSampleRolesIfValid(source.container, sampleRoleKey);
  const destinationSampleRoles = getContainerSampleRolesIfValid(
    destinationContainer,
    sampleRoleKey,
    destinationContainerSchemaId
  );
  if (sourceSampleRoles == null || destinationSampleRoles == null) {
    return null;
  }
  const idsToParties = objectFromEntries(
    [...sourceSampleRoles, ...destinationSampleRoles].map((sampleRole) => [sampleRole.id, sampleRole])
  );
  const uniqueIds = [...new Set(Object.keys(idsToParties))];
  return uniqueIds.map((id) => idsToParties[id]);
}

function getDestinationRestrictionStatus({
  source,
  destinationContainer,
  destinationContainerSchemaId,
}: {
  source: ContainerOrPlateWell | null;
  destinationContainer: ContainerOrInaccessible | null;
  destinationContainerSchemaId?: string;
}): SampleRestrictionStatus | null {
  const sourceRestrictionStatus =
    source == null
      ? SampleRestrictionStatus.UNRESTRICTED
      : isPlateWell(source)
        ? getPlateWellRestrictionStatusIfValid(source.plate, source.position)
        : getContainerRestrictionStatusIfValid(source.container);
  const destinationRestrictionStatus = getContainerRestrictionStatusIfValid(
    destinationContainer,
    destinationContainerSchemaId
  );
  if (sourceRestrictionStatus == null || destinationRestrictionStatus == null) {
    return null;
  }
  if (destinationRestrictionStatus === SampleRestrictionStatus.NOT_APPLICABLE) {
    return destinationRestrictionStatus;
  }
  if (
    destinationRestrictionStatus === SampleRestrictionStatus.RESTRICTED ||
    sourceRestrictionStatus === SampleRestrictionStatus.RESTRICTED
  ) {
    return SampleRestrictionStatus.RESTRICTED;
  } else {
    return SampleRestrictionStatus.UNRESTRICTED;
  }
}

function getServerResolutionResultFromSampleRoles(
  parties: Array<PartySummary> | null
): ServerResolutionResult {
  if (parties == null) {
    return makeCustomResolutionResultFromTypedValue(null);
  }
  const partiesTypedValue = getTypedValueFromPartySummaries(parties);
  return makeCustomResolutionResultFromTypedValue(partiesTypedValue);
}

function getServerResolutionResultFromRestrictionStatus(
  status: SampleRestrictionStatus | null
): ServerResolutionResult {
  if (status == null) {
    return makeCustomResolutionResultFromTypedValue(null);
  }
  const statusTypedValue = getTypedValueFromSampleRestrictionStatus(status);
  return makeCustomResolutionResultFromTypedValue(statusTypedValue);
}

async function fetchSourceAndDestinationContainers({
  sourceContainerId,
  destinationContainerId,
}: {
  sourceContainerId: string;
  destinationContainerId?: string;
}): Promise<{
  sourceContainer: ContainerOrInaccessible;
  destinationContainer: ContainerOrInaccessible | null;
}> {
  const data =
    destinationContainerId != null
      ? await stubbableFunctions.doSourceAndDestinationContainerQuery(
          sourceContainerId,
          destinationContainerId
        )
      : await stubbableFunctions.doContainerQuery(sourceContainerId);
  const sourceContainer = data.containers[0];
  const destinationContainer = destinationContainerId != null ? data.containers[1] : null;
  return {sourceContainer, destinationContainer};
}

async function fetchSourceAndDestinationContainersIfPresent({
  sourceContainerId,
  destinationContainerId,
}: {
  sourceContainerId?: string;
  destinationContainerId?: string;
}): Promise<{
  sourceContainer: ContainerOrInaccessible | null;
  destinationContainer: ContainerOrInaccessible | null;
}> {
  return sourceContainerId != null
    ? await fetchSourceAndDestinationContainers({
        sourceContainerId,
        destinationContainerId,
      })
    : {
        sourceContainer: null,
        destinationContainer: await fetchDestinationContainer({destinationContainerId}),
      };
}

function handleSampleRolesResolution({
  sampleRoleKey,
  sourceStorable,
  destinationContainer,
  destinationContainerSchemaId,
  isSpecifyingSampleOwners,
  isSpecifyingRestrictedSampleUsers,
  specifiedRestrictionStatus,
}: {
  sampleRoleKey: SampleRoleKey | null;
  sourceStorable: ContainerOrPlateWell | null;
  destinationContainer: ContainerOrInaccessible | null;
  destinationContainerSchemaId?: string;
  isSpecifyingSampleOwners: boolean | null;
  isSpecifyingRestrictedSampleUsers: boolean | null;
  specifiedRestrictionStatus: SampleRestrictionStatusTypedValue['status'] | null;
}): ServerResolutionResult {
  const isValidatingOnly =
    (sampleRoleKey === 'sampleOwners' && isSpecifyingSampleOwners) ||
    (sampleRoleKey === 'restrictedSampleUsers' && isSpecifyingRestrictedSampleUsers);
  if (destinationContainer && !canHaveSampleControls(destinationContainer)) {
    if (isValidatingOnly) {
      return {
        type: undefined,
        error: 'Sample roles cannot be set on a well plate well',
        resolvedValue: undefined,
      };
    }
    return getServerResolutionResultFromSampleRoles(null);
  }

  const sampleRoles =
    isValidatingOnly || !sampleRoleKey
      ? null
      : getUnionOfSampleRolesFromSourceAndDestination({
          source: sourceStorable,
          destinationContainer,
          sampleRoleKey,
          destinationContainerSchemaId,
        });

  const isSettingOrValidatingRestrictedSampleUsers =
    isSpecifyingRestrictedSampleUsers ||
    (sampleRoleKey === 'restrictedSampleUsers' && sampleRoles != null && sampleRoles.length > 0);

  if (isSettingOrValidatingRestrictedSampleUsers) {
    const {canHaveAuthUsers, possibleErrorText} = canHaveRestrictedSampleUsers(
      sourceStorable,
      destinationContainer,
      isSpecifyingSampleOwners ?? null,
      specifiedRestrictionStatus
    );
    if (!canHaveAuthUsers) {
      if (isSpecifyingRestrictedSampleUsers) {
        return {
          type: undefined,
          error: possibleErrorText,
          resolvedValue: undefined,
        };
      } else {
        return getServerResolutionResultFromSampleRoles(null);
      }
    }
  }

  return getServerResolutionResultFromSampleRoles(sampleRoles);
}

function handleRestrictionStatusResolution({
  sourceStorable,
  destinationContainer,
  destinationContainerSchemaId,
  specifiedRestrictionStatus,
}: {
  sourceStorable: ContainerOrPlateWell | null;
  destinationContainer: ContainerOrInaccessible | null;
  destinationContainerSchemaId?: string;
  specifiedRestrictionStatus: SampleRestrictionStatusTypedValue['status'] | null;
}): ServerResolutionResult {
  const isValidatingOnly = specifiedRestrictionStatus != null;
  if (isValidatingOnly) {
    if (
      destinationContainer &&
      !canHaveSampleControls(destinationContainer) &&
      specifiedRestrictionStatus !== SampleRestrictionStatus.NOT_APPLICABLE
    ) {
      return {
        type: undefined,
        error: 'Restriction status cannot be set on a well plate well',
        resolvedValue: undefined,
      };
    } else if (
      // No destination container means this is a create transfer table. You can't create fixed
      // plate wells in a transfer table, so the new container must support sample control.
      (!destinationContainer || canHaveSampleControls(destinationContainer)) &&
      specifiedRestrictionStatus === SampleRestrictionStatus.NOT_APPLICABLE
    ) {
      return {
        type: undefined,
        error: 'N/A is not a valid restriction status for this container',
        resolvedValue: undefined,
      };
    }
  }

  const restrictionStatus = isValidatingOnly
    ? null
    : getDestinationRestrictionStatus({
        source: sourceStorable,
        destinationContainer,
        destinationContainerSchemaId,
      });

  return getServerResolutionResultFromRestrictionStatus(restrictionStatus);
}

export async function getSampleRolesWithSourceOrDestinationContainer({
  sourceContainerId,
  sampleRoleKey,
  destinationContainerId,
  destinationContainerSchemaId,
  isSpecifyingSampleOwners,
  isSpecifyingRestrictedSampleUsers,
  specifiedRestrictionStatus,
}: {
  sourceContainerId?: string;
  sampleRoleKey: SampleRoleKey;
  destinationContainerId?: string;
  destinationContainerSchemaId?: string;
  isSpecifyingSampleOwners?: boolean | null;
  isSpecifyingRestrictedSampleUsers?: boolean | null;
  specifiedRestrictionStatus?: SampleRestrictionStatusTypedValue['status'] | null;
}): Promise<ServerResolutionResult> {
  const {sourceContainer, destinationContainer} = await fetchSourceAndDestinationContainersIfPresent({
    sourceContainerId,
    destinationContainerId,
  });

  return handleSampleRolesResolution({
    sourceStorable: sourceContainer ? {container: sourceContainer} : null,
    sampleRoleKey,
    destinationContainer,
    destinationContainerSchemaId,
    isSpecifyingSampleOwners: isSpecifyingSampleOwners ?? null,
    isSpecifyingRestrictedSampleUsers: isSpecifyingRestrictedSampleUsers ?? null,
    specifiedRestrictionStatus: specifiedRestrictionStatus ?? null,
  });
}

export async function getRestrictionStatusWithSourceOrDestinationContainer({
  sourceContainerId,
  destinationContainerId,
  destinationContainerSchemaId,
  specifiedRestrictionStatus,
}: {
  sourceContainerId?: string;
  destinationContainerId?: string;
  destinationContainerSchemaId?: string;
  specifiedRestrictionStatus: SampleRestrictionStatusTypedValue['status'] | null;
}): Promise<ServerResolutionResult> {
  const {sourceContainer, destinationContainer} = await fetchSourceAndDestinationContainersIfPresent({
    sourceContainerId,
    destinationContainerId,
  });

  return handleRestrictionStatusResolution({
    sourceStorable: sourceContainer ? {container: sourceContainer} : null,
    destinationContainer,
    destinationContainerSchemaId,
    specifiedRestrictionStatus,
  });
}

async function fetchDestinationContainer({
  destinationContainerId,
}: {
  destinationContainerId?: string;
}): Promise<ContainerOrInaccessible | null> {
  return destinationContainerId != null
    ? (await stubbableFunctions.doContainerQuery(destinationContainerId)).containers[0]
    : null;
}

async function getSourcePlateAndDestinationContainer({
  sourcePlateId,
  destinationContainerId,
}: {
  sourcePlateId: string;
  destinationContainerId?: string;
}): Promise<{sourcePlate: PlateOrInaccessible; destinationContainer: ContainerOrInaccessible | null}> {
  const sourceData = await stubbableFunctions.doPlateQuery(sourcePlateId);
  const sourcePlate = sourceData.plates[0];
  const destinationContainer = await fetchDestinationContainer({destinationContainerId});
  return {sourcePlate, destinationContainer};
}

export async function getSampleRolesWithSourcePlate({
  sourcePlateId,
  sourcePosition,
  sampleRoleKey,
  destinationContainerId,
  destinationContainerSchemaId,
  isSpecifyingSampleOwners,
  isSpecifyingRestrictedSampleUsers,
  specifiedRestrictionStatus,
}: {
  sourcePlateId: string;
  sourcePosition: string;
  sampleRoleKey: SampleRoleKey;
  destinationContainerId?: string;
  destinationContainerSchemaId?: string;
  isSpecifyingSampleOwners?: boolean | null;
  isSpecifyingRestrictedSampleUsers?: boolean | null;
  specifiedRestrictionStatus?: SampleRestrictionStatusTypedValue['status'] | null;
}): Promise<ServerResolutionResult> {
  const {sourcePlate, destinationContainer} = await getSourcePlateAndDestinationContainer({
    sourcePlateId,
    destinationContainerId,
  });

  return handleSampleRolesResolution({
    sourceStorable: {plate: sourcePlate, position: sourcePosition},
    isSpecifyingSampleOwners: isSpecifyingSampleOwners ?? null,
    isSpecifyingRestrictedSampleUsers: isSpecifyingRestrictedSampleUsers ?? null,
    specifiedRestrictionStatus: specifiedRestrictionStatus ?? null,
    sampleRoleKey,
    destinationContainer,
    destinationContainerSchemaId,
  });
}

export async function getRestrictionStatusWithSourcePlate({
  sourcePlateId,
  sourcePosition,
  destinationContainerId,
  destinationContainerSchemaId,
  specifiedRestrictionStatus,
}: {
  sourcePlateId: string;
  sourcePosition: string;
  destinationContainerId?: string;
  destinationContainerSchemaId?: string;
  specifiedRestrictionStatus: SampleRestrictionStatusTypedValue['status'] | null;
}): Promise<ServerResolutionResult> {
  const {sourcePlate, destinationContainer} = await getSourcePlateAndDestinationContainer({
    sourcePlateId,
    destinationContainerId,
  });

  return handleRestrictionStatusResolution({
    sourceStorable: {plate: sourcePlate, position: sourcePosition},
    specifiedRestrictionStatus,
    destinationContainer,
    destinationContainerSchemaId,
  });
}

export const SampleControlRolesResolver = {
  validateOrAutopopulateSampleOwnersFromSourceOrDestinationContainer: async (
    query: InventoryTableSampleOwnersFromSourceOrDestinationContainerQuery
  ): Promise<ServerResolutionResult> =>
    await getSampleRolesWithSourceOrDestinationContainer({
      sourceContainerId: query.sourceContainerId ?? undefined,
      sampleRoleKey: 'sampleOwners',
      destinationContainerId: query.destinationContainerId ?? undefined,
      destinationContainerSchemaId: query.destinationSchemaId ?? undefined,
      isSpecifyingSampleOwners: query.isSpecifyingSampleOwners,
    }),

  validateOrAutopopulateRestrictedSampleUsersFromSourceOrDestinationContainer: async (
    query: InventoryTableRestrictedSampleUsersFromSourceOrDestinationContainerQuery
  ): Promise<ServerResolutionResult> =>
    await getSampleRolesWithSourceOrDestinationContainer({
      sourceContainerId: query.sourceContainerId ?? undefined,
      sampleRoleKey: 'restrictedSampleUsers',
      destinationContainerId: query.destinationContainerId ?? undefined,
      destinationContainerSchemaId: query.destinationSchemaId ?? undefined,
      isSpecifyingSampleOwners: query.isSpecifyingSampleOwners,
      isSpecifyingRestrictedSampleUsers: query.isSpecifyingRestrictedSampleUsers,
      specifiedRestrictionStatus: query.specifiedRestrictionStatus,
    }),

  validateOrAutopopulateRestrictionStatusFromSourceOrDestinationContainer: async (
    query: InventoryTableRestrictionStatusFromSourceOrDestinationContainerQuery
  ): Promise<ServerResolutionResult> =>
    await getRestrictionStatusWithSourceOrDestinationContainer({
      sourceContainerId: query.sourceContainerId ?? undefined,
      destinationContainerId: query.destinationContainerId ?? undefined,
      destinationContainerSchemaId: query.destinationSchemaId ?? undefined,
      specifiedRestrictionStatus: query.specifiedRestrictionStatus,
    }),

  validateOrAutopopulateSampleOwnersFromSourcePlate: async (
    query: InventoryTableSampleOwnersFromSourcePlateQuery
  ): Promise<ServerResolutionResult> =>
    await getSampleRolesWithSourcePlate({
      sourcePlateId: query.sourcePlateId,
      sourcePosition: query.sourcePosition,
      sampleRoleKey: 'sampleOwners',
      destinationContainerId: query.destinationContainerId ?? undefined,
      destinationContainerSchemaId: query.destinationSchemaId ?? undefined,
      isSpecifyingSampleOwners: query.isSpecifyingSampleOwners,
    }),

  validateOrAutopopulateRestrictedSampleUsersFromSourcePlate: async (
    query: InventoryTableRestrictedSampleUsersFromSourcePlateQuery
  ): Promise<ServerResolutionResult> =>
    await getSampleRolesWithSourcePlate({
      sourcePlateId: query.sourcePlateId,
      sourcePosition: query.sourcePosition,
      sampleRoleKey: 'restrictedSampleUsers',
      destinationContainerId: query.destinationContainerId ?? undefined,
      destinationContainerSchemaId: query.destinationSchemaId ?? undefined,
      isSpecifyingSampleOwners: query.isSpecifyingSampleOwners,
      isSpecifyingRestrictedSampleUsers: query.isSpecifyingRestrictedSampleUsers,
      specifiedRestrictionStatus: query.specifiedRestrictionStatus,
    }),

  validateOrAutopopulateRestrictionStatusFromSourcePlate: async (
    query: InventoryTableRestrictionStatusFromSourcePlateQuery
  ): Promise<ServerResolutionResult> =>
    await getRestrictionStatusWithSourcePlate({
      sourcePlateId: query.sourcePlateId,
      sourcePosition: query.sourcePosition,
      destinationContainerId: query.destinationContainerId ?? undefined,
      destinationContainerSchemaId: query.destinationSchemaId ?? undefined,
      specifiedRestrictionStatus: query.specifiedRestrictionStatus,
    }),
};

function extractRestrictionDependencies({
  cellDependencies,
  numStorableColumns,
}: {
  cellDependencies: Array<TypedValue | null>;
  numStorableColumns: number;
}): {
  isSpecifyingSampleOwners: boolean | null;
  specifiedRestrictionStatus: SampleRestrictionStatusTypedValue['status'] | null;
} {
  const hasRestrictionDependencyColumn = cellDependencies.length > numStorableColumns;
  const restrictionDependencyTypedVal = cellDependencies[numStorableColumns];
  let isSpecifyingSampleOwners = null;
  let specifiedRestrictionStatus = null;
  if (hasRestrictionDependencyColumn) {
    if (hasMinimumSCRFeatureLevel(SampleControlsFeatureLevel.V2)) {
      specifiedRestrictionStatus =
        restrictionDependencyTypedVal?.type === BaseTypedValueType.SAMPLE_RESTRICTION_STATUS
          ? restrictionDependencyTypedVal.status
          : SampleRestrictionStatus.UNRESTRICTED;
    } else {
      isSpecifyingSampleOwners = getPartyIdsFromTypedValue(cellDependencies[numStorableColumns]).length > 0;
    }
  }
  return {isSpecifyingSampleOwners, specifiedRestrictionStatus};
}

function extractSourceAndDestinationContainerIds({
  hasDestinationContainer,
  hasSourceContainer,
  cellDependencies,
}: {
  hasDestinationContainer: boolean;
  hasSourceContainer: boolean;
  cellDependencies: Array<TypedValue | null>;
}): {sourceContainerId: string | null; destinationContainerId: string | null; numOfContainerColumns: number} {
  if (!(hasSourceContainer || hasDestinationContainer)) {
    return {sourceContainerId: null, destinationContainerId: null, numOfContainerColumns: 0};
  }
  assert(
    (!(hasDestinationContainer && hasSourceContainer) && cellDependencies.length >= 1) ||
      cellDependencies.length > 1,
    'Specified container typed values must be present'
  );
  const numOfContainerColumns = hasSourceContainer && hasDestinationContainer ? 2 : 1;
  const sourceContainerId = hasSourceContainer ? extractContainerId(cellDependencies[0]) : null;
  const destinationContainerId = hasDestinationContainer
    ? extractContainerId(cellDependencies[numOfContainerColumns - 1])
    : null;
  return {sourceContainerId, destinationContainerId, numOfContainerColumns};
}

function extractSourcePlateAndDestinationContainerIds({
  hasDestinationContainer,
  cellDependencies,
}: {
  hasDestinationContainer: boolean;
  cellDependencies: Array<TypedValue | null>;
}): {
  sourcePlateId: string | null;
  sourcePosition: string | null;
  destinationContainerId: string | null;
  numStorableColumns: number;
} {
  const numStorableColumns = hasDestinationContainer ? 3 : 2;
  assert(cellDependencies.length >= numStorableColumns, 'Specified storable typed values must be present');
  const sourcePlateId = extractPlateId(cellDependencies[0]);
  const sourcePosition =
    cellDependencies[1] == null || isTypedValueEmpty(cellDependencies[1])
      ? null
      : formatTypedValue(cellDependencies[1]);
  const destinationContainerId = hasDestinationContainer
    ? extractContainerId(cellDependencies[numStorableColumns - 1])
    : null;
  return {sourcePlateId, sourcePosition, destinationContainerId, numStorableColumns};
}

function validateSampleControlColumnsFromSourceOrDestinationContainer({
  hasDestinationContainer,
  hasSourceContainer,
  cellDependencies,
  typedValue,
}: {
  hasDestinationContainer: boolean;
  hasSourceContainer: boolean;
  cellDependencies: Array<TypedValue | null>;
  typedValue: TypedValue;
}): {
  sourceContainerId: string | null;
  destinationContainerId: string | null;
  numOfContainerColumns: number;
  shortCircuitReturnVal: ValWithErrorObject | null;
} {
  const {sourceContainerId, destinationContainerId, numOfContainerColumns} =
    extractSourceAndDestinationContainerIds({
      hasSourceContainer,
      hasDestinationContainer,
      cellDependencies,
    });

  // Skip sending the custom resolution query if there is an error during extraction of source/dest, or if
  // the source and destination have not yet been set and the user has not entered a value,
  // meaning no validation needed
  let shortCircuitReturnVal = null;
  if (
    (hasSourceContainer && !sourceContainerId) ||
    (hasDestinationContainer && !destinationContainerId) ||
    (!hasSourceContainer && !hasDestinationContainer && isTypedValueEmpty(typedValue))
  ) {
    const emptyTypedVal: StringTypedValue = {type: BaseTypedValueType.STRING, rawValue: ''};
    shortCircuitReturnVal = {
      val: isTypedValueEmpty(typedValue) ? emptyTypedVal : typedValue,
      error: null,
      locked: false,
    };
  }
  return {sourceContainerId, destinationContainerId, numOfContainerColumns, shortCircuitReturnVal};
}

function validateSampleControlColumnsFromSourcePlate({
  hasDestinationContainer,
  cellDependencies,
  typedValue,
}: {
  hasDestinationContainer: boolean;
  cellDependencies: Array<TypedValue | null>;
  typedValue: TypedValue;
}): {
  sourcePlateId: string | null;
  sourcePosition: string | null;
  destinationContainerId: string | null;
  numStorableColumns: number;
  shortCircuitReturnVal: ValWithErrorObject | null;
} {
  const {sourcePlateId, sourcePosition, destinationContainerId, numStorableColumns} =
    extractSourcePlateAndDestinationContainerIds({
      hasDestinationContainer,
      cellDependencies,
    });

  // Skip sending the custom resolution query if there is an error during extraction of source/dest or source/
  // dest values have not yet been set, so validation is not yet needed
  let shortCircuitReturnVal = null;
  if (!sourcePlateId || !sourcePosition || (hasDestinationContainer && !destinationContainerId)) {
    const emptyTypedVal: StringTypedValue = {type: BaseTypedValueType.STRING, rawValue: ''};
    shortCircuitReturnVal = {
      val: isTypedValueEmpty(typedValue) ? emptyTypedVal : typedValue,
      error: null,
      locked: false,
    };
  }
  return {
    sourcePlateId,
    sourcePosition,
    destinationContainerId,
    numStorableColumns,
    shortCircuitReturnVal,
  };
}

function _getSampleOwnersFromSourceOrDestinationContainerCustomEval<CustomEvalOptions>({
  storableSchemaId,
  hasDestinationContainer,
  hasSourceContainer,
}: {
  storableSchemaId?: string | null;
  hasDestinationContainer: boolean;
  hasSourceContainer: boolean;
}): CustomEval<CustomEvalOptions> {
  return (
    typedValue: TypedValue,
    columnDefinition: PartiesColumnDefinition,
    cellDependencies: Array<TypedValue | null>,
    customEvalOptions: unknown,
    resolveFromResolutionQuery: ResolveQueryFn
  ) => {
    const {sourceContainerId, destinationContainerId, shortCircuitReturnVal} =
      validateSampleControlColumnsFromSourceOrDestinationContainer({
        hasSourceContainer,
        hasDestinationContainer,
        typedValue,
        cellDependencies,
      });
    if (shortCircuitReturnVal != null) {
      return shortCircuitReturnVal;
    }

    const {resolvedValue, error} = resolveFromResolutionQuery(
      typedValue,
      {
        customResolutionQueryType:
          CustomResolutionQueryType.INVENTORY_TABLE_SAMPLE_OWNERS_FROM_SOURCE_OR_DESTINATION_CONTAINER,
        sourceContainerId,
        destinationContainerId,
        destinationSchemaId: storableSchemaId ?? null,
        isSpecifyingSampleOwners: !isTypedValueEmpty(typedValue),
        type: ResolutionQueryType.CUSTOM,
      },
      makeTypedValueFromCustomResolvedValue
    );
    return {val: !isTypedValueEmpty(typedValue) ? typedValue : resolvedValue, error, locked: false};
  };
}

function _getSampleOwnersFromSourcePlateCustomEval<CustomEvalOptions>({
  storableSchemaId,
  hasDestinationContainer,
}: {
  storableSchemaId?: string | null;
  hasDestinationContainer: boolean;
}): CustomEval<CustomEvalOptions> {
  return (
    typedValue: TypedValue,
    columnDefinition: PartiesColumnDefinition,
    cellDependencies: Array<TypedValue | null>,
    customEvalOptions: unknown,
    resolveFromResolutionQuery: ResolveQueryFn
  ) => {
    const {sourcePlateId, sourcePosition, destinationContainerId, shortCircuitReturnVal} =
      validateSampleControlColumnsFromSourcePlate({hasDestinationContainer, cellDependencies, typedValue});
    if (shortCircuitReturnVal != null) {
      return shortCircuitReturnVal;
    }
    assert(
      sourcePlateId != null && sourcePosition != null,
      'Validation should have already short circuited for null plate/position'
    );

    const {resolvedValue, error} = resolveFromResolutionQuery(
      typedValue,
      {
        customResolutionQueryType: CustomResolutionQueryType.INVENTORY_TABLE_SAMPLE_OWNERS_FROM_SOURCE_PLATE,
        sourcePlateId,
        sourcePosition,
        destinationContainerId,
        destinationSchemaId: storableSchemaId ?? null,
        isSpecifyingSampleOwners: !isTypedValueEmpty(typedValue),
        type: ResolutionQueryType.CUSTOM,
      },
      makeTypedValueFromCustomResolvedValue
    );
    return {val: !isTypedValueEmpty(typedValue) ? typedValue : resolvedValue, error, locked: false};
  };
}

function _getRestrictionStatusFromSourceOrDestinationContainerCustomEval<CustomEvalOptions>({
  storableSchemaId,
  hasDestinationContainer,
  hasSourceContainer,
}: {
  storableSchemaId?: string | null;
  hasDestinationContainer: boolean;
  hasSourceContainer: boolean;
}): CustomEval<CustomEvalOptions> {
  return (
    typedValue: TypedValue,
    columnDefinition: PartiesColumnDefinition,
    cellDependencies: Array<TypedValue | null>,
    customEvalOptions: unknown,
    resolveFromResolutionQuery: ResolveQueryFn,
    resolveTypedValueToType: ResolveTypedValueToTypeFn
  ) => {
    const {sourceContainerId, destinationContainerId, shortCircuitReturnVal} =
      validateSampleControlColumnsFromSourceOrDestinationContainer({
        hasSourceContainer,
        hasDestinationContainer,
        typedValue,
        cellDependencies,
      });
    if (shortCircuitReturnVal != null) {
      return shortCircuitReturnVal;
    }

    // TODO(BNCH-15744): customEval currently runs _before_ string resolution and this particular resolution
    // would like to operate on a post-resolved value, so we manually fire it off first.
    const {resolvedValue: resolvedTypedValue, error: resolutionError} = resolveTypedValueToType(
      columnDefinition,
      typedValue
    );
    if (resolutionError || resolvedTypedValue == null) {
      return {val: resolvedTypedValue ?? typedValue, error: resolutionError, locked: false};
    }

    const {resolvedValue: customResolvedValue, error} = resolveFromResolutionQuery(
      resolvedTypedValue,
      {
        customResolutionQueryType:
          CustomResolutionQueryType.INVENTORY_TABLE_RESTRICTION_STATUS_FROM_SOURCE_OR_DESTINATION_CONTAINER,
        sourceContainerId,
        destinationContainerId,
        destinationSchemaId: storableSchemaId ?? null,
        specifiedRestrictionStatus:
          resolvedTypedValue.type === BaseTypedValueType.SAMPLE_RESTRICTION_STATUS
            ? resolvedTypedValue.status
            : null,
        type: ResolutionQueryType.CUSTOM,
      },
      makeTypedValueFromCustomResolvedValue
    );
    return {
      // Return the string resolved value if it's not empty; otherwise, the value from custom eval
      val: !isTypedValueEmpty(resolvedTypedValue) ? resolvedTypedValue : customResolvedValue,
      error,
      locked: false,
    };
  };
}

function _getRestrictionStatusFromSourcePlateCustomEval<CustomEvalOptions>({
  storableSchemaId,
  hasDestinationContainer,
}: {
  storableSchemaId?: string | null;
  hasDestinationContainer: boolean;
}): CustomEval<CustomEvalOptions> {
  return (
    typedValue: TypedValue,
    columnDefinition: PartiesColumnDefinition,
    cellDependencies: Array<TypedValue | null>,
    customEvalOptions: unknown,
    resolveFromResolutionQuery: ResolveQueryFn,
    resolveTypedValueToType: ResolveTypedValueToTypeFn
  ) => {
    const {sourcePlateId, sourcePosition, destinationContainerId, shortCircuitReturnVal} =
      validateSampleControlColumnsFromSourcePlate({hasDestinationContainer, cellDependencies, typedValue});
    if (shortCircuitReturnVal != null) {
      return shortCircuitReturnVal;
    }
    assert(
      sourcePlateId != null && sourcePosition != null,
      'Validation should have already short circuited for null plate/position'
    );

    // TODO(BNCH-15744): customEval currently runs _before_ string resolution and this particular resolution
    // would like to operate on a post-resolved value, so we manually fire it off first.
    const {resolvedValue: resolvedTypedValue, error: resolutionError} = resolveTypedValueToType(
      columnDefinition,
      typedValue
    );
    if (resolutionError || resolvedTypedValue == null) {
      return {val: resolvedTypedValue ?? typedValue, error: resolutionError, locked: false};
    }

    const {resolvedValue: customResolvedValue, error} = resolveFromResolutionQuery(
      resolvedTypedValue,
      {
        customResolutionQueryType:
          CustomResolutionQueryType.INVENTORY_TABLE_RESTRICTION_STATUS_FROM_SOURCE_PLATE,
        sourcePlateId,
        sourcePosition,
        destinationContainerId,
        destinationSchemaId: storableSchemaId ?? null,
        specifiedRestrictionStatus:
          resolvedTypedValue.type === BaseTypedValueType.SAMPLE_RESTRICTION_STATUS
            ? resolvedTypedValue.status
            : null,
        type: ResolutionQueryType.CUSTOM,
      },
      makeTypedValueFromCustomResolvedValue
    );
    return {
      // Return the string resolved value if it's not empty; otherwise, the value from custom eval
      val: !isTypedValueEmpty(resolvedTypedValue) ? resolvedTypedValue : customResolvedValue,
      error,
      locked: false,
    };
  };
}

function _getRestrictedSampleUsersFromSourceOrDestinationContainerCustomEval<CustomEvalOptions>({
  storableSchemaId,
  hasDestinationContainer,
  hasSourceContainer,
}: {
  storableSchemaId?: string | null;
  hasDestinationContainer: boolean;
  hasSourceContainer: boolean;
}): CustomEval<CustomEvalOptions> {
  return (
    typedValue: TypedValue,
    columnDefinition: PartiesColumnDefinition,
    cellDependencies: Array<TypedValue | null>,
    customEvalOptions: unknown,
    resolveFromResolutionQuery: ResolveQueryFn
  ) => {
    const {sourceContainerId, destinationContainerId, numOfContainerColumns, shortCircuitReturnVal} =
      validateSampleControlColumnsFromSourceOrDestinationContainer({
        hasSourceContainer,
        hasDestinationContainer,
        typedValue,
        cellDependencies,
      });
    if (shortCircuitReturnVal != null) {
      return shortCircuitReturnVal;
    }

    const {isSpecifyingSampleOwners, specifiedRestrictionStatus} = extractRestrictionDependencies({
      cellDependencies,
      numStorableColumns: numOfContainerColumns,
    });

    const {resolvedValue, error} = resolveFromResolutionQuery(
      typedValue,
      {
        customResolutionQueryType:
          CustomResolutionQueryType.INVENTORY_TABLE_RESTRICTED_SAMPLE_USERS_FROM_SOURCE_OR_DESTINATION_CONTAINER,
        sourceContainerId,
        destinationContainerId,
        destinationSchemaId: storableSchemaId ?? null,
        isSpecifyingSampleOwners,
        specifiedRestrictionStatus,
        isSpecifyingRestrictedSampleUsers: !isTypedValueEmpty(typedValue),
        type: ResolutionQueryType.CUSTOM,
      },
      makeTypedValueFromCustomResolvedValue
    );

    return {val: !isTypedValueEmpty(typedValue) ? typedValue : resolvedValue, error, locked: false};
  };
}

function _getRestrictedSampleUsersFromSourcePlateCustomEval<CustomEvalOptions>({
  storableSchemaId,
  hasDestinationContainer,
}: {
  storableSchemaId?: string | null;
  hasDestinationContainer: boolean;
}): CustomEval<CustomEvalOptions> {
  return (
    typedValue: TypedValue,
    columnDefinition: PartiesColumnDefinition,
    cellDependencies: Array<TypedValue | null>,
    customEvalOptions: unknown,
    resolveFromResolutionQuery: ResolveQueryFn
  ) => {
    const {sourcePlateId, sourcePosition, destinationContainerId, numStorableColumns, shortCircuitReturnVal} =
      validateSampleControlColumnsFromSourcePlate({hasDestinationContainer, cellDependencies, typedValue});
    if (shortCircuitReturnVal != null) {
      return shortCircuitReturnVal;
    }
    assert(
      sourcePlateId != null && sourcePosition != null,
      'Validation should have already short circuited for null plate/position'
    );

    const {isSpecifyingSampleOwners, specifiedRestrictionStatus} = extractRestrictionDependencies({
      cellDependencies,
      numStorableColumns,
    });

    const {resolvedValue, error} = resolveFromResolutionQuery(
      typedValue,
      {
        customResolutionQueryType:
          CustomResolutionQueryType.INVENTORY_TABLE_RESTRICTED_SAMPLE_USERS_FROM_SOURCE_PLATE,
        sourcePlateId,
        sourcePosition,
        destinationContainerId,
        destinationSchemaId: storableSchemaId ?? null,
        isSpecifyingRestrictedSampleUsers: !isTypedValueEmpty(typedValue),
        isSpecifyingSampleOwners,
        specifiedRestrictionStatus,
        type: ResolutionQueryType.CUSTOM,
      },
      makeTypedValueFromCustomResolvedValue
    );
    return {val: !isTypedValueEmpty(typedValue) ? typedValue : resolvedValue, error, locked: false};
  };
}

export const SampleControlRolesCustomEval = {
  getSampleOwnersFromSourceOrDestinationContainer: lruMemoize(
    _getSampleOwnersFromSourceOrDestinationContainerCustomEval,
    {max: 100},
    (...args) => stringify(args)
  ),

  getRestrictedSampleUsersFromSourceOrDestinationContainer: lruMemoize(
    _getRestrictedSampleUsersFromSourceOrDestinationContainerCustomEval,
    {max: 100},
    (...args) => stringify(args)
  ),

  getRestrictionStatusFromSourceOrDestinationContainer: lruMemoize(
    _getRestrictionStatusFromSourceOrDestinationContainerCustomEval,
    {max: 100},
    (...args) => stringify(args)
  ),

  getSampleOwnersFromSourcePlate: lruMemoize(
    _getSampleOwnersFromSourcePlateCustomEval,
    {max: 100},
    (...args) => stringify(args)
  ),

  getRestrictedSampleUsersFromSourcePlate: lruMemoize(
    _getRestrictedSampleUsersFromSourcePlateCustomEval,
    {max: 100},
    (...args) => stringify(args)
  ),

  getRestrictionStatusFromSourcePlate: lruMemoize(
    _getRestrictionStatusFromSourcePlateCustomEval,
    {max: 100},
    (...args) => stringify(args)
  ),
};
